import type { DiscoveryGetContentDetailsResponse } from "@amzn/ads-discovery-theme-service-typescript-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { isEmpty, isUndefined } from "lodash";
import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";
import { useStore } from "zustand";

import { ComplianceState } from "../../../../api/adsApi/adsApi.interfaces";
import { getDiscoveryApiClient } from "../../../../api/discoveryApi/discoveryApi";
import { INSIGHT_DETAILS_TRIGGER_TEXT } from "../../../../config/insights.config";
import { useAsyncErrorStatusNotification } from "../../../../hooks/notifications/useAsyncErrorStatusNotification";
import type { AssociatedPitchedContent } from "../../../../store/editContent";
import { editContentStore } from "../../../../store/editContent";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { globalWorkbenchStore } from "../../../../store/globalWorkbench";
import { insightDetailsStore } from "../../../../store/insightDetails";
import { reportIssueStore } from "../../../../store/reportIssue";
import {
    defaultContentVersion,
    defaultExternalShareInfo,
    shareContentStore,
} from "../../../../store/shareContent";
import { workbenchStore } from "../../../../store/workbench";
import { AquilaThumbsFeedbackPlacement } from "../../../../types/aquila";
import type { CardDetail } from "../../../../types/card";
import type { DownloadAsset } from "../../../../types/common";
import { ContentPreviewType } from "../../../../types/content";
import { isLoading } from "../../../../utils/api.utils";
import {
    getInsightPreviewFileFormat,
    isMediaFile,
} from "../../../../utils/insightPreview.utils";
import { AquilaThumbsFeedback } from "../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { StyledActionsContainer } from "../../commonStyles";
import { InsightSelectionActionText } from "../InsightSelectionActionText";

export interface PitchedContentCardActionProps {
    details: CardDetail[];
    content: AssociatedPitchedContent;
    isEditable: boolean;
    isSharable: boolean;
}

export const PitchedContentCardAction: FunctionComponent<
    PitchedContentCardActionProps
> = ({ details, content, isEditable, isSharable }) => {
    const { contentId, contentTitle, fileFormat } = content;

    const previewFileFormat = getInsightPreviewFileFormat(fileFormat);

    const [setEditContent, showEditModal] = useStore(
        editContentStore,
        (state) => [state.setContent, state.showEditModal],
    );
    const [thumbsFeedbackCard, globalWorkbench] = useStore(
        FeatureFlagsStore,
        (state) => [state.thumbsFeedbackCard, state.globalWorkbench],
    );

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);

    const [setInsightDetails, setAsyncStatus, resetInsightDetails] = useStore(
        insightDetailsStore,
        (state) => [
            state.setInsightDetails,
            state.setAsyncStatus,
            state.resetState,
        ],
    );

    const [previewAsset, setPreviewAsset] = useState<DownloadAsset | undefined>(
        undefined,
    );
    const [downloadAsset, setDownloadAsset] = useState<
        DownloadAsset | undefined
    >(undefined);

    const [contentDetails, setContentDetails] = useState<
        DiscoveryGetContentDetailsResponse | undefined
    >(undefined);

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [updateReportIssueContextData] = useStore(
        reportIssueStore,
        (state) => [state.updateReportIssueContextData],
    );

    const [
        setExternalShareInfo,
        setIdentifier,
        setVersion,
        setShareModalOpen,
        setOriginalFileFormat,
    ] = useStore(shareContentStore, (state) => [
        state.setExternalShareInfo,
        state.setIdentifier,
        state.setVersion,
        state.setIsModalOpen,
        state.setOriginalFileFormat,
    ]);

    const getDownloadData = async () => {
        return await (
            await getDiscoveryApiClient()
        ).getContentDetails(contentId!, ContentPreviewType.ORIGINAL, true);
    };

    const [previewState, previewAction] = useAsync(async () => {
        const previewType = isMediaFile(previewFileFormat)
            ? ContentPreviewType.ORIGINAL
            : ContentPreviewType.PDF;
        const data: DiscoveryGetContentDetailsResponse = await (
            await getDiscoveryApiClient()
        ).getContentDetails(contentId!, previewType, false);

        const downloadData =
            previewType === ContentPreviewType.PDF
                ? await getDownloadData()
                : data;

        setDownloadAsset(downloadData as DownloadAsset);
        setPreviewAsset(data as DownloadAsset);
        setContentDetails(data);
    });
    useAsyncErrorStatusNotification(previewState);

    const handleInsightDetails = (): void => {
        void previewAction.execute();
    };

    const handleEditInsight = () => {
        setEditContent(content);
        showEditModal();
    };

    const handleShareInsight = () => {
        const externalShareDate =
            contentDetails!.content?.externalShareExpiryDate;
        const validExternalShare =
            externalShareDate && new Date(externalShareDate) > new Date();
        setExternalShareInfo({
            ...defaultExternalShareInfo,
            ...(validExternalShare && contentDetails!.content),
        });
        setVersion(
            contentDetails!.content?.latestVersion ?? defaultContentVersion,
        );
        setIdentifier(contentId!);
        setOriginalFileFormat(fileFormat!);
        setShareModalOpen(true);
    };

    useEffect(() => {
        if (!isUndefined(contentDetails)) {
            if (isEditable) {
                setEditContent(content);
            }
            updateReportIssueContextData({
                identifier: contentId ?? "",
                title: contentTitle ?? "",
                insightType: InsightType.PITCHED_CONTENT,
            });
            setInsightDetails({
                open: true,
                info: {
                    title: contentTitle!,
                    details,
                    previewFileFormat,
                    insightType: InsightType.PITCHED_CONTENT,
                    identifier: contentId!,
                    previewAsset,
                    downloadAsset: {
                        downloadUrl: downloadAsset?.downloadUrl,
                        content: {
                            bucket: "",
                            key: "",
                        },
                        contentId: contentDetails.content?.contentId ?? "",
                        contentVersion: contentDetails.content
                            ? contentDetails.content?.latestVersion?.toString()
                            : "1",
                    },
                    previewLoading: isLoading(previewState.status),
                    compliance: {
                        isCompliant:
                            contentDetails.content?.workbenchComplianceState ===
                            ComplianceState.COMPLIANT,
                        status:
                            contentDetails.content?.workbenchComplianceState ??
                            ComplianceState.UNSUPPORTED_PPTX,
                    },
                    ...(isEditable && { handleEditInsight }),
                    ...(isSharable && { handleShareInsight }),
                    isShareEnabled: !isMediaFile(previewFileFormat),
                },
            });
        } else {
            resetInsightDetails();
        }
    }, [
        previewAsset,
        downloadAsset,
        contentDetails,
        previewState.status,
        previewFileFormat,
    ]);
    const numSelectedSlides = globalWorkbench
        ? getSlidesSelected(contentId!).length
        : getSlideIndexes(contentId!).size;

    return (
        <StyledActionsContainer>
            <TextButton
                disabled={isEmpty(contentId)}
                onClick={handleInsightDetails}
            >
                {INSIGHT_DETAILS_TRIGGER_TEXT}
            </TextButton>
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={contentId!}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && thumbsFeedbackCard && (
                <AquilaThumbsFeedback
                    identifier={contentId!}
                    insightType={InsightType.PITCHED_CONTENT}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                />
            )}
        </StyledActionsContainer>
    );
};
